import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useCreateManyBoardDirectories } from '@/api/hooks/boardDirectoriesHooks';
import { useUpdateNonProfitOrgWiki } from '@/api/hooks/nonProfitOrgHooks';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import DirectorForm from '@/components/forms/DirectorForm';
import FormWrapper from '@/components/forms/FormWrapper';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { BoardOfDirectorsData, boardOfDirectorsResolver, DirectorData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';

const NpoBoardDirectors = () => {
  const { initialList, directorDataJson, directorIndex, npoId } = useLocalSearchParams<{
    initialList: string;
    directorDataJson: string;
    directorIndex: string;
    npoId: string;
  }>();

  const [npoIdState, setNpoIdState] = useState(npoId);

  const { state: authState } = useAuth();
  const { mutateAsync: createManyBoardDirectories, isPending: boardDirectoriesPending } =
    useCreateManyBoardDirectories();
  const { mutateAsync: updateNpoWiki, isPending: updateNpoPending } = useUpdateNonProfitOrgWiki();

  const { showSuccess, showError } = useEndFlowModal();
  const { setVisible, setNpoId } = useWikiChangesNpoModal();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  useEffect(() => {
    if (npoId) setNpoIdState(npoId);
  }, [npoId]);

  useEffect(() => {
    if (directorDataJson) {
      const directorData = safeJsonParse<DirectorData>(directorDataJson);
      if (directorData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !directorIndex && !fields.find((field) => objectComparision(field, directorData))
          ? append(directorData)
          : update(+directorIndex, directorData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directorDataJson]);

  const isIndividual = authState.authReady && authState.user?.user.role === 'INDIVIDUAL';
  const isChurch = authState.authReady && authState.signUpData?.loginEmail?.type === 'CHURCH';

  const {
    control,
    handleSubmit,
    trigger,
    clearErrors,
    getValues,
    getFieldState,
    formState: { isValid, submitCount, errors }
  } = useForm<BoardOfDirectorsData>({
    defaultValues: { boardDirectories: initialList !== undefined ? safeJsonParse<DirectorData[]>(initialList) : [] },
    mode: 'onSubmit',
    resolver: boardOfDirectorsResolver,
    context: { isIndividual, isChurch }
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'boardDirectories'
  });

  const onSubmit: SubmitHandler<BoardOfDirectorsData> = async (data) => {
    const mappedData = data.boardDirectories.map((director) => ({
      ...director,
      nonProfitOrgId: npoIdState ?? authState.signUpData?.loginEmail?.npoId ?? ''
    }));
    const body = {
      boardDirectories: mappedData,
      npoId: npoIdState ?? authState.signUpData?.loginEmail?.npoId
    };

    try {
      if (isIndividual) {
        updateNpoWiki(
          {
            npoId: npoIdState ?? '',
            usersBoardDirectory: body.boardDirectories
          },
          {
            onSuccess: () => {
              setNpoId(npoIdState ?? '');
              setVisible(true);
            },
            onError: (e) => {
              showError({ message: e.message });
              console.error('Error', e);
            }
          }
        );
      } else {
        createManyBoardDirectories(body, {
          onSuccess: () => {
            showSuccess({
              title: i18n.t('end_flow.success_title'),
              message: i18n.t('npo_details.update_message')
            });
            router.back();
          },
          onError: (e) => {
            showError({ message: e.message });
            console.error('Error', e);
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.board_directors.title')}
          description={i18n.t('sign_in.nonProfit.board_directors.description')}>
          {getValues('boardDirectories') && getValues('boardDirectories').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 160 - bottom - top }}>
              <DirectorForm
                initialData={getValues('boardDirectories.0')}
                showDoneButton
                showBackButton={false}
                isLoading={boardDirectoriesPending}
                manualValidation={isChurch}
                onDone={(data, triggerDirector) => {
                  if (!data.fullName && !data.position && !getFieldState('boardDirectories').isDirty) {
                    if (isChurch) {
                      clearErrors();
                      handleSubmit(onSubmit)();
                    }
                    return;
                  }
                  if (data.fullName && data.position) {
                    clearErrors();
                    insert(0, data);
                    handleSubmit(onSubmit)();
                  } else if (!data.fullName || !data.position || data.allowance || data.annualSalary) {
                    triggerDirector();
                  }
                }}
                onAdd={(data) => {
                  clearErrors();
                  update(0, data);
                  trigger('boardDirectories.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/edit/addUpdateDirector');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.done')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(director) => director.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/edit/addUpdateDirector',
                        params: {
                          directorDataJson: JSON.stringify(field.item),
                          directorIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.fullName}
                    subtitle={field.item.position ?? ''}
                    budget={field.item.annualSalary}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/edit/addUpdateDirector')}
                    label={i18n.t('sign_in.nonProfit.board_directors.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        <View style={styles.buttonsContainer}>
          {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

          {getValues('boardDirectories') && getValues('boardDirectories').length > 0 && (
            <BrandedButton
              loading={boardDirectoriesPending || updateNpoPending}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {i18n.t('mix.next')}
            </BrandedButton>
          )}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  flexOne: {
    flex: 1
  },

  buttonsContainer: {
    flexDirection: 'column',
    width: '100%'
  },

  errorText: {
    color: 'red',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },

  divider: {
    marginBottom: 15
  }
});

export default NpoBoardDirectors;
